import React, { useState, useEffect, } from 'react';
import * as bp4 from '@blueprintjs/core';
import * as bp4d from '@blueprintjs/datetime2';
import * as bp4s from '@blueprintjs/select';
import { Formik } from 'formik';
import { toXML } from 'jstoxml';
import styles from './MissioForm.module.css';
import moment from "moment";
import dioceses from './data/dioceses.json';
// import projectNumbers from './data/projectNumbers.json';
import xml2json from "./xml2json";
import languages from "./languages";
import DOMPurify from 'dompurify';

function SafeHtml({ html }: { html: string }) {
  const safeHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong'],
    ALLOWED_ATTR: [],
  });

  return <div dangerouslySetInnerHTML={{ __html: safeHtml.toString() }} />;
}

function generateUUID() { // Public Domain/MIT
    var d = new Date().getTime();//Timestamp
    var d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16;//random number between 0 and 16
        if (d > 0) {//Use timestamp until depleted
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {//Use microseconds since page-load if supported
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
}

type Entry = {
    id: string,
    projektnummer: string,
    created: string,
    familienname: string,
    vorname: string,
    gebdat: string,
    dioezese: string,
    orden: string,
    studienjahr: number,
    studienjahrText: string,
    aufnahme: number,
    pastoraljahr: number,
    diakonweihe: number,
    priesterweihe: number,
    sprache: string,
    bild: File | null,
    bildB64: string | null,
    lebenslauf: string,
    hintergrund: string,
    berufung: string,
    wichtiges: string,
    sonstiges: string
};

const createEmptyEntry: () => Entry = () => {
    const currentYear = (new Date()).getFullYear();
    return {
        id: generateUUID(),
        projektnummer: '',
        created: moment().format('DD.MM.YYYY'),
        familienname: '',
        vorname: '',
        gebdat: '',
        dioezese: '',
        orden: '',
        studienjahr: 1,
        studienjahrText: '',
        aufnahme: 0,
        pastoraljahr: 0,
        diakonweihe: 0,
        priesterweihe: 0,
        sprache: '',
        bild: null,
        bildB64: null,
        lebenslauf: '',
        hintergrund: '',
        berufung: '',
        wichtiges: '',
        sonstiges: ''
    };
};

// Function to generate XML
async function generateXML(values: Entry, language: string) {
    const {
        projektnummer,
        created,
        familienname,
        vorname,
        gebdat,
        dioezese,
        orden,
        studienjahr,
        studienjahrText,
        aufnahme,
        pastoraljahr,
        diakonweihe,
        priesterweihe,
        bild,
        bildB64,
        lebenslauf,
        hintergrund,
        berufung,
        wichtiges,
        sonstiges
    } = values;

    return toXML({
        datenblatt: {
            priesterseminar: {
                projektnummer,
                created
            },
            seminarist: {
                familienname,
                vorname,
                gebdat,
                dioezese,
                orden,
                studienjahr: studienjahrText !== '' ? studienjahrText : studienjahr,
                aufnahme,
                pastoraljahr,
                diakonweihe,
                priesterweihe,
                sprache: language,
                bild: (bild && await toBase64(bild)) || bildB64
            },
            beschreibung: {
                lebenslaufOriginal: lebenslauf,
                lebenslauf,
                hintergrundOriginal: hintergrund,
                hintergrund,
                berufungOriginal: berufung,
                berufung,
                wichtigesOriginal: wichtiges,
                wichtiges,
                sonstigesOriginal: sonstiges,
                sonstiges
            }
        }
    }, {
        header: true,
        indent: ' '
    });
}

// Function to handle form submission
async function handleFormSubmission(values: Entry, language: string, downloadXML: boolean) {
    const { projektnummer, created } = values;
    const {
        familienname,
        vorname,
        gebdat,
        dioezese,
        orden,
        studienjahr,
        studienjahrText,
        aufnahme,
        pastoraljahr,
        diakonweihe,
        priesterweihe,
        bild,
        bildB64
    } = values;
    const {
        lebenslauf,
        hintergrund,
        berufung,
        wichtiges,
        sonstiges
    } = values;

    const validationErrors = validateForm(values);
    if (Object.keys(validationErrors).length === 0) {
        const xml = generateXML(values, language);

        if (downloadXML) {
            download(`${vorname} ${familienname}.xml`, await xml)
        } else {
            return xml
        }
    }

}


function download(filename: string, text: string) {
    var element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
}

const toBase64 = (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

function unique(arr: any[]) {
    var u = {}, a = [];
    for (var i = 0, l = arr.length; i < l; ++i) {
        if (!u.hasOwnProperty(arr[i])) {
            a.push(arr[i]);
            // @ts-ignore
            u[arr[i]] = 1;
        }
    }
    return a;
}
function showSpinner() {
    let spinner = document.getElementById("bp4-spinner");
    if (spinner) {
        spinner.style.display = "flex";
    }
}

function hideSpinner() {
    let spinner = document.getElementById("bp4-spinner");
    if (spinner) {
        spinner.style.display = "none";
    }
}

function showCallout(status: string, message: string) {
    let callout = (status == "success") ? document.getElementById("bp4-callout--success") : document.getElementById("bp4-callout--error");
    if (callout) {
        let deepChild = callout.querySelector("#bp4-callout__text");
        if (deepChild) deepChild.textContent = message;

        callout.style.display = "block";
        setTimeout(() => {
            if (callout) callout.style.display = "none";
        }, 5000);
    }

}

async function sendToServer(xmlStringPromise: Promise<string | undefined>, language: string) {

    showSpinner();
    const submit_button = document.getElementById("submit-button") as HTMLButtonElement;
    if (submit_button) {
        submit_button.disabled = true;
    }
    var status = '';
    try { 
        const xmlString = await xmlStringPromise;
        if (xmlString) {
            const response = await fetch("https://www.missio.at/seminarian/wp-json/seminar/v1/create-seminarist", {
                method: "POST",
                body: xmlString,
                redirect: "follow",
            })
            // const response = await fetch("https://missiosem.ameisenhaufen.dev/seminarian/wp-json/seminar/v1/create-seminarist", {
            //     method: "POST",
            //     body: xmlString,
            //     redirect: "follow",
            // })
            
            if (response.ok) {
                // Handle successful response
                hideSpinner();
                const responseData = await response.json();
                showCallout("success", responseData[language]);
            
                status = 'success';

                if (submit_button) {
                    submit_button.disabled = false;
                }                
            } else {
                hideSpinner();
                console.log('XML string is empty', response.status);
                showCallout("error", response.status + " " + languages.statusFailed);
            
                status = 'error';

                if (submit_button) {
                    submit_button.disabled = false;
                }
            }
        } else {
            hideSpinner();
            showCallout("error", languages.statusFailed);
            
            status = 'error';

            if (submit_button) {
                submit_button.disabled = false;
            }
        }
    } catch (error) {
        hideSpinner();
        console.log('Error:', error);
        showCallout("error", languages.statusFailed);

        status = 'error';

        if (submit_button) {
            submit_button.disabled = false;
        }
    }

    return status;
}

async function translate(text: string): Promise<string | null> {
    try {
        const body = new URLSearchParams();
        body.append('text', text);
        body.append('target_lang', 'de');
        const response = await fetch('https://api-free.deepl.com/v2/translate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': `DeepL-Auth-Key ${'5f05887b-1e09-7c8e-7354-ecc3be7aa050:fx'}`
            },
            body
        });
        if (200 !== response.status) {
            const errorMsg = `Error (Code: ${response.status}}: ${await response.text()}`;
            console.log(errorMsg);
            return errorMsg;
        }
        const { translations: { text: translatedText } } = await response.json();
        return translatedText;
    } catch (e) {
        console.log('error during translation');
        console.log(e);
        return `Error (Unknown): ${e}`;
    }
}

// Function to validate form values
function validateForm(values: Entry) {
    const errors: any = {};
    const {
        familienname,
        vorname,
        studienjahr,
        aufnahme,
        pastoraljahr,
        diakonweihe,
        priesterweihe,
        gebdat,
        projektnummer,
        dioezese,
        lebenslauf,
        hintergrund,
        berufung,
        wichtiges,
        bild,
        bildB64
    } = values;


    if (!familienname || familienname === "") {
        errors.familienname = languages.errorIsRequired;
    }
    if (!vorname || vorname === "") {
        errors.vorname = languages.errorIsRequired;
    }
    if (!studienjahr || isNaN(studienjahr)) {
        errors.studienjahr = languages.errorIsRequired;
    }
    if (!aufnahme || isNaN(aufnahme)) {
        errors.aufnahme = languages.errorIsRequired;
    }
    if (!pastoraljahr || isNaN(pastoraljahr)) {
        errors.pastoraljahr = languages.errorIsRequired;
    }
    if (!diakonweihe || isNaN(diakonweihe)) {
        errors.diakonweihe = languages.errorIsRequired;
    }
    if (!priesterweihe || isNaN(priesterweihe)) {
        errors.priesterweihe = languages.errorIsRequired;
    }
    if (!gebdat || gebdat === "") {
        errors.gebdat = languages.errorGebdat;
    }
    if (!projektnummer || projektnummer === "") {
        errors.projektnummer = languages.errorProjektnummer;
    }
    if (!dioezese || dioezese === "") {
        errors.dioezese = languages.errorDioezese;
    }
    if (!lebenslauf || lebenslauf === "") {
        errors.lebenslauf = languages.errorIsRequired;
    }
    if (!hintergrund || hintergrund === "") {
        errors.hintergrund = languages.errorIsRequired;
    }
    if (!berufung || berufung === "") {
        errors.berufung = languages.errorIsRequired;
    }
    if (!wichtiges || wichtiges === "") {
        errors.wichtiges = languages.errorIsRequired;
    }
    if (!bild && !bildB64) {
        errors.bild = languages.errorMissingUpload;
    }
    return errors;
}

const handleFormsClear = (resetForm: () => void, setFieldValue: (field: string, value: any) => void, values: Entry) => {
    resetForm();
    setFieldValue('sprache', values.sprache);
    setFieldValue('projektnummer', values.projektnummer);
    setFieldValue('studienjahr', values.studienjahr);
    setFieldValue('aufnahme', values.aufnahme);
    setFieldValue('pastoraljahr', values.pastoraljahr);
    setFieldValue('diakonweihe', values.diakonweihe);
    setFieldValue('priesterweihe', values.priesterweihe);
};

export default function MissioForm() {
    const [xmlFile, setXmlFile] = React.useState<File | null>(null);
    const [importError, setImportError] = React.useState<string | null>(null);
    const userLocale =
        navigator.languages && navigator.languages.length
            ? navigator.languages[0]
            : navigator.language;
    const [language, setLanguage] = React.useState<string>(
        ["en", "fr", "pt", "es"].includes(userLocale.substring(0, 2))
            ? userLocale.substring(0, 2)
            : "en"
    );
    const [projectNumbers, setProjects] = useState<any[]>([]);
    const [showImport, setShowImport] = useState<boolean>(false);
    const togglImport = () => setShowImport(!showImport);

    const setData = async () => {
        const apiUrl = "https://www.missio.at/seminarian/wp-json/wp/v2/projects?per_page=100&filter[orderby]=meta_value_num&meta_key=id&order=asc";
        fetch(apiUrl)
        .then(data => data.json())
        .then(data => {
            let projects:any;
            projects = []

            data.forEach(function (item: any) {
                projects.push({
                    id: item.acf.id, 
                    name:  item.acf.name,
                    url:  item.acf.url,
                    lang:  item.acf.lang
                });   
            });  

            setProjects(projects);
        })
    }

    useEffect(() => {
        setData();
    }, [])

    const regexp = /[&+]/g;


    return <div className={styles.missioForm}>
        <Formik initialValues={createEmptyEntry()} onSubmit={(values, props) => {
            handleFormSubmission(values, language, false)
        }} 
            validate={validateForm}>
            {({
                values,
                handleSubmit,
                handleChange,
                setFieldValue,
                resetForm,
                setErrors,
                setSubmitting,
                setValues,
                errors,
                touched
                
            }) => <form onSubmit={handleSubmit}>
                    <div className={styles.entryPanel}>
                        <div className={styles.leftPanel}>
                            <bp4.RadioGroup className={styles.langSelect} label={languages.languageName}
                                onChange={e => {
                                    languages.setLanguage(e.currentTarget.value);
                                    setLanguage(e.currentTarget.value);
                                }}
                                selectedValue={language}>
                                {languages.getAvailableLanguages().map(language => {
                                    
                                    
                                    return(<bp4.Radio
                                    key={language}
                                    label={languages.getString(language)}
                                    value={language} />)
                                })}
                            </bp4.RadioGroup>
                            <bp4.Divider className={styles.divider} />
                            <bp4.FormGroup label={languages.project} labelInfo={languages.required}>
                                {/*<bp4.InputGroup name="projektnummer"
                                            required
                                            value={values.projektnummer}
                                            onChange={e => setFieldValue('projektnummer', e.currentTarget.value)}/>*/}
                                <bp4s.Select<{ id: number, name: string }>
                                    fill
                                    activeItem={values.projektnummer ? projectNumbers.find(({ id }) => `${id}` === values.projektnummer) : null}
                                    items={projectNumbers}
                                    itemPredicate={(query, item) => item.name.toLowerCase().includes(query.toLowerCase()) || `${item.id}`.includes(query)}
                                    onItemSelect={
                                        ({ id }) => {
                                            setFieldValue('projektnummer', `${id}`);

                                            var selectedId = `${id}`;
                                            var lang = projectNumbers.find(({ id }) => `${id}` === selectedId)?.lang;
                                            if(lang){
                                                languages.setLanguage(lang);
                                                setLanguage(lang);
                                            }
                                        }
                                }
                                    itemRenderer={({ id, name }, props) => {
                                        return props.modifiers.matchesPredicate && !values.projektnummer.includes(name) ?
                                            <bp4.MenuItem key={`${name} (${id})`} onClick={props.handleClick} text={`${name} (${id})`} /> : null;
                                    }}>
                                    <bp4.Button
                                        fill
                                        rightIcon="double-caret-vertical"
                                        placeholder={languages.projectNumberPlaceholder}
                                        text={values.projektnummer ? `${projectNumbers.find(({ id }) => `${id}` === values.projektnummer)?.name} (${values.projektnummer})` : languages.projectNumberPlaceholder}  
                                        />                  
                                </bp4s.Select>
                                {touched.projektnummer && errors.projektnummer && <p style={{ color: "red" }}>{errors.projektnummer}</p>}
                            </bp4.FormGroup>
                            <bp4.FormGroup className={styles.datefield} label={languages.creationDate} labelInfo={languages.required}>
                                <bp4d.DateInput3 value={(() => {
                                    return values.created !== '' ? moment(values.created, 'DD.MM.YYYY').toDate().toISOString() : null;
                                })()}
                                    minDate={moment('1.1.1900', 'DD.MM.YYYY').toDate()}
                                    maxDate={moment('1.1.3000', 'DD.MM.YYYY').toDate()}
                                    onChange={e => setFieldValue('created', moment(e, 'YYYY-MM-DD').format('DD.MM.YYYY'))}
                                    disabled={true}
                                    parseDate={(str) => {
                                        return str === '' ? null : moment(str, 'DD.MM.YYYY').toDate();
                                    }}
                                    formatDate={(date) => moment(date).format('DD.MM.YYYY')} />
                            </bp4.FormGroup>
                            <h2>{languages.student_priest}:</h2>
                            <bp4.FormGroup label={languages.lastName} labelInfo={languages.asInPassportNoTitle}>
                                <bp4.InputGroup name="familienname"
                                    required
                                    value={values.familienname}
                                    onChange={e => setFieldValue('familienname', e.currentTarget.value)} />
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.firstName} labelInfo={languages.asInPassport}>
                                <bp4.InputGroup name="vorname"
                                    required
                                    value={values.vorname}
                                    onChange={e => setFieldValue('vorname', e.currentTarget.value)} />
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.dateOfBirth} labelInfo={languages.required}>
                                <bp4d.DateInput3 
                                    value={(() => {
                                        return values.gebdat ? moment(values.gebdat, 'DD.MM.YYYY').toDate().toISOString() : null;
                                    })()}     
                                    minDate={moment('1.1.1900', 'DD.MM.YYYY').toDate()}
                                    maxDate={moment().add(-18, 'years').toDate()}
                                    onChange={e => {
                                        setFieldValue('gebdat', e ? moment(e, 'YYYY-MM-DD').format('DD.MM.YYYY') : null);
                                    }}
                                    initialMonth={values.gebdat ? moment(values.gebdat, 'DD.MM.YYYY').toDate() : moment().add(-18, 'years').toDate()}
                                    parseDate={(str) => {
                                        return str ? null : moment(str, 'DD.MM.YYYY').toDate();
                                    }}
                                    locale={language}
                                    dayPickerProps={{
                                        weekStartsOn: 1,
                                    }}
                                    formatDate={(date) => moment(date).format('DD.MM.YYYY')} 
                                    />
                                {touched.gebdat && errors.gebdat && <p style={{ color: "red" }}>{errors.gebdat}</p>}
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.diocese} labelInfo={languages.required}>
                                <bp4s.Select<{ cc: string, name: string }>
                                    fill
                                    activeItem={values.dioezese ? dioceses.find(({ name }) => `${name}` === values.dioezese) : null}
                                    items={dioceses}
                                    itemPredicate={(query, item) => {
                                        const result = item.name.toLowerCase().includes(query.toLowerCase()) || `${item.cc}`.includes(query);
                                        if (result && query === 'Oran')
                                            console.log(`query=${query} name=${item.name} cc=${item.cc}`);
                                        return result;
                                    }}
                                    onItemSelect={({ name }) => setFieldValue('dioezese', `${name}`)}
                                    itemRenderer={({ cc, name }, props) => {
                                        return props.modifiers.matchesPredicate ?
                                            <bp4.MenuItem key={`${name}-(${cc})`} onClick={props.handleClick}
                                                text={`${name} (${cc})`} /> : null;
                                    }}>
                                    <bp4.Button
                                        fill
                                        rightIcon="double-caret-vertical"
                                        text={values.dioezese ? `${dioceses.find(({ name }) => `${name}` === values.dioezese)?.name} (${dioceses.find(({ name }) => `${name}` === values.dioezese)?.cc})` : ''} />
                                </bp4s.Select>
                                {touched.dioezese && errors.dioezese && <p style={{ color: "red" }}>{errors.dioezese}</p>}
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.congregation}>
                                <bp4.InputGroup name="orden"
                                    value={values.orden}
                                    onChange={e => setFieldValue('orden', e.currentTarget.value)} />
                            </bp4.FormGroup>
                            <bp4.Divider  className={styles.divider}/>
                            <bp4.FormGroup label={languages.yearOfTheology} labelInfo={languages.required}>
                                {values.studienjahrText === '' && <bp4.NumericInput value={values.studienjahr}
                                    required
                                    onValueChange={v => setFieldValue('studienjahr', v)}
                                    fill
                                    disabled={values.studienjahrText !== ''}
                                    min={1}
                                    max={4}
                                    minorStepSize={1} />}
                                {/*<bp4.Checkbox label={'Deacon'} checked={values.studienjahrText !== ''} onChange={(e) => {
                                setFieldValue('studienjahrText', e.currentTarget.checked? 'Deacon' : '');
                                setFieldValue('studienjahr', 5);
                            }} />*/}
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.yearOfAdmission} labelInfo={languages.required}>
                                <bp4.NumericInput 
                                    // value={values.aufnahme}
                                    required
                                    onValueChange={v => setFieldValue('aufnahme', v)}
                                    placeholder = {languages.yearPlaceholder}
                                    fill
                                    onChange={handleChange}
                                    min={2000}
                                    max={(new Date().getFullYear() + 50)} />
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.pastoralYear} labelInfo={languages.required}>
                                <bp4.NumericInput 
                                    // value={values.pastoraljahr}
                                    placeholder = {languages.yearPlaceholder}
                                    onValueChange={v => setFieldValue('pastoraljahr', v)}
                                    fill
                                    required
                                    min={2000}
                                    max={(new Date().getFullYear() + 50)} />
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.yearOfOrdinationToDiaconate} labelInfo={languages.required}>
                                <bp4.NumericInput 
                                    // value={values.diakonweihe}
                                    placeholder = {languages.yearPlaceholder}
                                    onValueChange={v => setFieldValue('diakonweihe', v)}
                                    fill
                                    required
                                    min={2000}
                                    max={(new Date().getFullYear() + 50)} />
                            </bp4.FormGroup>
                            <bp4.FormGroup label={languages.yearOfOrdination} labelInfo={languages.required}>
                                <bp4.NumericInput name="priesterweihe"
                                    // value={values.priesterweihe}
                                    placeholder = {languages.yearPlaceholder}
                                    onValueChange={v => setFieldValue('priesterweihe', v)}
                                    fill
                                    required
                                    min={2000}
                                    max={(new Date().getFullYear() + 50)} />
                            </bp4.FormGroup>
                            {/* Form language used instead */}
                            {/*<bp4.FormGroup label={languages.languages}>

                            <bp4s.Select
                                               fill
                                               activeItem={values.sprache[0]}
                                               items={['FR', 'EN', 'ES', 'PT']}
                                               onItemSelect={(item) =>
                                                   setFieldValue('sprache', [item])}
                                               itemRenderer={(item, props) => {
                                                   return props.modifiers.matchesPredicate && !values.sprache.includes(item) ?
                                                       <bp4.MenuItem
                                                           itemID={item}
                                                           key={item}
                                                           onClick={props.handleClick}
                                                           {...props}
                                                           text={item}/>
                                                       : null;
                                               }}>
                                <bp4.Button
                                    fill
                                    rightIcon="double-caret-vertical"
                                    text={values.sprache && values.sprache[0] && values.sprache[0]}/>
                            </bp4s.Select>
                        </bp4.FormGroup>*/}
                            <bp4.Divider  className={styles.divider} />
                            <h2>DESCRIPTION:</h2>
                            <bp4.FormGroup>
                                <label title={languages.cvPlaceholder} >{languages.cv} <span className="icon-circle">i</span> <span className="bp5-text-muted">{languages.required}</span></label>
                                <p>{languages.oneFiftyInfo}</p>
                                <bp4.TextArea value={values.lebenslauf}
                                    fill
                                    placeholder={languages.cvPlaceholder}
                                    required
                                    title={languages.cvPlaceholder}
                                    onChange={e => {
                                        const sanitizedValue = e.currentTarget.value.replace(regexp, '');
                                        setFieldValue('lebenslauf', sanitizedValue);
                                    }} />
                            </bp4.FormGroup>
                            <bp4.FormGroup>
                                <label title={languages.backgroundPlaceholder}>{languages.background} <span className="icon-circle">i</span> <span className="bp5-text-muted">{languages.required}</span></label>
                                <p>{languages.eightyInfo}</p>
                                <bp4.TextArea value={values.hintergrund}
                                    fill
                                    required
                                    title={languages.backgroundPlaceholder}
                                    placeholder={languages.backgroundPlaceholder}
                                    onChange={e => {
                                        const sanitizedValue = e.currentTarget.value.replace(regexp, '');
                                        setFieldValue('hintergrund', sanitizedValue);
                                    }} />
                            </bp4.FormGroup>
                            <bp4.FormGroup>
                                <label title={languages.berufungPlaceholder}>{languages.berufung} <span className="icon-circle">i</span> <span className="bp5-text-muted">{languages.required}</span></label>
                                <p>{languages.eightyInfo}</p>
                                <bp4.TextArea value={values.berufung}
                                    fill
                                    required
                                    title={languages.berufungPlaceholder}
                                    placeholder={languages.berufungPlaceholder}
                                    onChange={e => {
                                        const sanitizedValue = e.currentTarget.value.replace(regexp, '');
                                        setFieldValue('berufung', sanitizedValue);
                                    }} />
                            </bp4.FormGroup>
                            <bp4.FormGroup>
                                <label title={languages.wichtigesPlaceholder}>{languages.wichtiges} <span className="icon-circle">i</span> <span className="bp5-text-muted">{languages.required}</span></label>
                                <p>{languages.eightyInfo}</p>
                                <bp4.TextArea value={values.wichtiges}
                                    fill
                                    required
                                    title={languages.wichtigesPlaceholder}
                                    placeholder={languages.wichtigesPlaceholder}
                                    onChange={e => {
                                        const sanitizedValue = e.currentTarget.value.replace(regexp, '');
                                        setFieldValue('wichtiges', sanitizedValue);
                                    }} />
                            </bp4.FormGroup>
                            <bp4.FormGroup>
                            <label title={languages.sonstigesPlaceholder}>{languages.sonstiges} <span className="icon-circle">i</span></label>
                                <bp4.TextArea value={values.sonstiges}
                                    fill
                                    title={languages.sonstigesPlaceholder}
                                    placeholder={languages.sonstigesPlaceholder}
                                    onChange={e => {
                                        const sanitizedValue = e.currentTarget.value.replace(regexp, '');
                                        setFieldValue('sonstiges', sanitizedValue);
                                    }} />
                            </bp4.FormGroup>
                        </div>
                        <div className={styles.rightPanel}>
                            <bp4.FormGroup label={languages.profile} labelInfo={languages.required}>
                                <bp4.FileInput text={languages.choose_file} fill buttonText={languages.browse} inputProps={{ accept: "image/png, image/jpeg, image/bmp, image/webp, image/heic, image/heif" }} onInputChange={e => {
                                    const file = e.currentTarget.files?.item(0);
                                    setFieldValue('bild', file);
                                }} />
                            </bp4.FormGroup>
                            <bp4.Divider  className={styles.divider} />
                            {values.bild ? <img
                                style={{ width: 300 }}
                                src={values.bild ? URL.createObjectURL(values.bild) : values.bildB64 || undefined}
                                alt={languages.profile} /> : errors.bild && <p style={{ color: "red" }}>{languages.errorMissingUpload}</p>}
                            
                            <br />
                            <div>
                                <p>{languages.photoExplanation}</p>
                                <ul>
                                    <li><SafeHtml html={languages.photoExplanation1} /></li>
                                    <li><SafeHtml html={languages.photoExplanation2} /></li>
                                    <li><SafeHtml html={languages.photoExplanation3} /></li>
                                    <li><SafeHtml html={languages.photoExplanation4} /></li>
                                    <li><SafeHtml html={languages.photoExplanation5} /></li>
                                    <li><SafeHtml html={languages.photoExplanation6} /></li>
                                    <li><SafeHtml html={languages.photoExplanation7} /></li>
                                    <li><SafeHtml html={languages.photoExplanation8} /></li>
                                </ul>

                            </div>
                            {/* <bp4.Divider />
                            <h3>Import <bp4.AnchorButton intent="none" outlined icon="expand-all" onClick={togglImport} /></h3>
                            <bp4.Collapse isOpen={showImport}>
                                <bp4.FormGroup label="Import" labelInfo={languages.xmlImportDescription}>
                                    <bp4.FileInput text={xmlFile ? xmlFile.name : "Choose file"} fill onInputChange={e => {
                                        const file = e.currentTarget.files?.item(0);
                                        setXmlFile(file ? file : null);
                                    }} />
                                </bp4.FormGroup>
                                <bp4.Button onClick={async () => {
                                    try {
                                        if (xmlFile === null)
                                            return;
                                        const rawXml = await xmlFile.text();
                                        const XmlNode = new DOMParser().parseFromString(rawXml, 'text/xml');
                                        const parsedData = xml2json(XmlNode);
                                        const {
                                            datenblatt: {
                                                priesterseminar: {
                                                    projektnummer,
                                                    created
                                                },
                                                seminarist: {
                                                    aufnahme,
                                                    bild,
                                                    diakonweihe,
                                                    dioezese,
                                                    familienname,
                                                    gebdat,
                                                    orden,
                                                    pastoraljahr,
                                                    priesterweihe,
                                                    sprache,
                                                    studienjahr,
                                                    vorname
                                                },
                                                beschreibung: {
                                                    berufung,
                                                    hintergrund,
                                                    lebenslauf,
                                                    sonstiges,
                                                    wichtiges
                                                }
                                            }
                                        } = parsedData;
                                        await setValues({
                                            id: '',
                                            created: created || null,
                                            projektnummer: projektnummer || null,
                                            familienname: familienname || null,
                                            vorname: vorname || null,
                                            gebdat: gebdat || null,
                                            dioezese: dioezese || null,
                                            orden: orden || null,
                                            studienjahr: studienjahr || null,
                                            studienjahrText: '',
                                            aufnahme: aufnahme || null,
                                            pastoraljahr: pastoraljahr || null,
                                            diakonweihe: diakonweihe || null,
                                            priesterweihe: priesterweihe || null,
                                            sprache: sprache || null,
                                            bild: null,
                                            bildB64: bild || null,
                                            lebenslauf: lebenslauf || null,
                                            hintergrund: hintergrund || null,
                                            berufung: berufung || null,
                                            wichtiges: wichtiges || null,
                                            sonstiges: sonstiges || null
                                        }, false);
                                    } catch (e) {
                                        setImportError(`${e}`);
                                    }
                                }} intent={bp4.Intent.PRIMARY} icon="export" text={languages.import} />
                                {/* {errors.bild && <p style={{color: "red"}}>{errors.bild}</p>} */
                                /*}
                                </div>{importError &&
                                    <bp4.NonIdealState icon="error" title="Fehler!" description={importError} />}
                            </bp4.Collapse> }*/}

                            <bp4.Divider className={styles.divider}/>
                            <p style={{fontSize: '120%', fontWeight: 'bold'}}>
                                    {languages.formatString(languages.privacyHint, {
                                        link: <a href="https://www.missio.at/seminarian/data-protection-information-privacy-policy/" target="_blank" rel="noreferrer">{languages.privacyHintLinkText}</a>
                                    })}
                                </p>
                            <bp4.ButtonGroup className={styles.finalSubmit}>
                                {/* <bp4.Button type="submit" intent={bp4.Intent.PRIMARY} icon="download"
                                    onClick={() => handleFormSubmission(values, language, true)}
                                >
                                    {languages.export}
                                </bp4.Button>
                                <bp4.Button
                                    intent={bp4.Intent.WARNING}
                                    onClick={() => handleFormsClear(resetForm, setFieldValue, values)}
                                    icon="clean"
                                >
                                    {languages.clear}
                                </bp4.Button> */}
                                
                                <bp4.Button id="submit-button" type="submit" intent={bp4.Intent.PRIMARY} icon="open-application" 
                                    onClick={() => {
                                        const validationErrors = validateForm(values);
                                        
                                        // console.log(validationErrors, values);
                                        if (Object.keys(validationErrors).length === 0) {
                                            const submit_status = sendToServer(handleFormSubmission(values, language, false), language);

                                            
                                            submit_status.then((value) => {
                                                if(value=='success'){
                                                    setSubmitting(false);
                                                    handleFormsClear(resetForm, setFieldValue, values);
                                                    setErrors({});
                                                }
                                            });
                                                
                                           
                                        }
                                    }}
                                >
                                    {languages.send}
                                </bp4.Button>
                            </bp4.ButtonGroup>
                            <br />
                            <bp4.Spinner id='bp4-spinner' size={50} intent={bp4.Intent.PRIMARY} />
                            <br />

                            <bp4.Callout id='bp4-callout--success' className='bp4-callout_status bp4-callout--success' title={languages.statusSuccess} intent="success"
                                icon="endorsed">
                                <span id="bp4-callout__text">{languages.statusSuccess}</span></bp4.Callout>

                            <bp4.Callout id='bp4-callout--error' className='bp4-callout_status bp4-callout--error' title={languages.statusFailed} intent="danger"
                                icon="error">
                                <span id="bp4-callout__text">{languages.statusFailed}</span></bp4.Callout>
                        </div>
                    </div>
                </form>}
        </Formik>
    </div>;
}